import React, { useState, useEffect } from 'react';
import {
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsItem
} from '../ui';
import { FileText, Receipt, Zap, RefreshCw, ExternalLink, CreditCard } from 'lucide-react';
import { EnhancedInvoiceQueue } from '../invoices/EnhancedInvoiceQueue';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../lib/hooks/useToast';
import { format } from 'date-fns';
import { formatCurrency } from '../../lib/utils';

interface EnhancedFattureInCloudSectionProps {
  dealId: string;
  translationPrefix?: any;
}

interface IssuedInvoice {
  id: string;
  fattureincloud_id: number;
  type: string;
  number: string;
  date: string;
  status: string;
  amount: number;
  paid_amount: number;
  net_amount?: number;
  vat_amount?: number;
  vat_rate?: number;
  data: any;
  deal_service_id?: string;
  installment_index?: number;
  fattureincloud_payments?: Array<{
    id: string;
    amount: number;
    date: string;
    payment_method: string;
  }>;
}

// FIX F-07 Finup 2026-07-10: il "netto" FIC (amount_net) include anche le righe esenti art.15
// (rimborsi bolli/spese anticipate). Mostrarlo come "Imponibile" confondeva l'utente, che lo
// confrontava con il valore dell'offerta. Scorporiamo gli esenti per mostrare l'imponibile vero.
// Stessa logica di InvoiceDetailModal / RPC: IVA 0% + vat.id Art.15, escluso "Bollo in fattura".
const EXEMPT_VAT_IDS = [21, 12163584, 32, 23];
const getInvoiceExemptAmount = (invoice: IssuedInvoice): number => {
  const items = invoice.data?.items_list || invoice.data?.items || [];
  return items
    .filter((item: any) =>
      (!item.vat?.value || item.vat.value === 0) &&
      EXEMPT_VAT_IDS.includes(item.vat?.id) &&
      item.name !== 'Bollo in fattura'
    )
    .reduce((sum: number, item: any) => {
      const qty = item.qty || 1;
      const unitPrice = item.net_price || 0;
      return sum + (item.net_cost || (qty * unitPrice));
    }, 0);
};

export function EnhancedFattureInCloudSection({
  dealId,
  translationPrefix
}: EnhancedFattureInCloudSectionProps) {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<'queue' | 'issued'>('queue');
  const [issuedInvoices, setIssuedInvoices] = useState<IssuedInvoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);

  // Load issued invoices linked to this deal
  const loadIssuedInvoices = async () => {
    try {
      setLoadingInvoices(true);
      console.log('[EnhancedFIC] Loading issued invoices for deal:', dealId);

      // Una fattura divisa su più offerte ha un solo deal_id (l'ultima collegata): le altre
      // offerte la vedono solo tramite invoice_allocations (Finup FT 205/201, 05/10/2026)
      const { data: allocations } = await supabase
        .from('invoice_allocations')
        .select('invoice_id')
        .eq('deal_id', dealId);
      const allocatedInvoiceIds = [...new Set((allocations || []).map((a) => a.invoice_id))];
      const dealFilter = allocatedInvoiceIds.length > 0
        ? `deal_id.eq.${dealId},id.in.(${allocatedInvoiceIds.join(',')})`
        : `deal_id.eq.${dealId}`;

      const { data, error } = await supabase
        .from('fattureincloud_invoices')
        .select(`
          id,
          fattureincloud_id,
          type,
          number,
          date,
          status,
          amount,
          paid_amount,
          net_amount,
          vat_amount,
          vat_rate,
          data,
          deal_service_id,
          installment_index,
          fattureincloud_payments:fattureincloud_payments(
            id,
            amount,
            date,
            payment_method
          )
        `)
        .or(dealFilter)
        .order('date', { ascending: false });

      if (error) {
        console.error('[EnhancedFIC] Error loading invoices:', error);
        throw error;
      }

      console.log(`[EnhancedFIC] Loaded ${data?.length || 0} issued invoices`);
      setIssuedInvoices(data || []);
    } catch (error) {
      console.error('[EnhancedFIC] Error loading issued invoices:', error);
      toast.error('Errore nel caricamento delle fatture emesse');
    } finally {
      setLoadingInvoices(false);
    }
  };

  useEffect(() => {
    if (dealId) {
      loadIssuedInvoices();
    }
  }, [dealId]);

  // Get status color - Notion style
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-[rgba(15,123,108,0.1)] text-[#0F7B6C] dark:bg-green-900/30 dark:text-green-300';
      case 'not_paid':
        return 'bg-[rgba(203,145,47,0.1)] text-[#CB912F] dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'reversed':
        return 'bg-[rgba(212,76,71,0.1)] text-[#D44C47] dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-[rgba(55,53,47,0.08)] text-[#787774] dark:bg-gray-700 dark:text-gray-300';
    }
  };

  // Get status label
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'paid':
        return 'Pagata';
      case 'not_paid':
        return 'Non pagata';
      case 'reversed':
        return 'Stornata';
      default:
        return status;
    }
  };

  // Get document type label
  const getDocumentTypeLabel = (type: string) => {
    switch (type) {
      case 'invoice':
        return 'Fattura';
      case 'quote':
        return 'Preventivo';
      case 'proforma':
        return 'Proforma';
      case 'receipt':
        return 'Ricevuta';
      case 'credit_note':
        return 'Nota di Credito';
      default:
        return type;
    }
  };

  // Get taxation type display - Notion style
  const getTaxationDisplay = (invoice: IssuedInvoice) => {
    const vatRate = invoice.vat_rate || 22;
    return {
      label: `IVA ${vatRate}%`,
      color: 'text-[#2383E2]',
      bgColor: 'bg-[rgba(35,131,226,0.1)] dark:bg-blue-900/30'
    };
  };

  return (
    <div className="border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-lg bg-white dark:bg-[#2F3437]">
      {/* Header - Notion style compatto */}
      <div className="flex items-center justify-between p-4 pb-3 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-[#2383E2]" />
          <h3 className="text-sm font-medium text-[#37352F] dark:text-white">Fatture in Cloud</h3>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'queue' | 'issued')}>
          <TabsList className="grid w-full grid-cols-2 bg-[rgba(55,53,47,0.04)] dark:bg-gray-800 rounded-md p-0.5">
            <TabsItem value="queue" className="flex items-center justify-center gap-1.5 text-xs min-h-[32px] data-[state=active]:bg-white data-[state=active]:shadow-sm dark:data-[state=active]:bg-gray-700 rounded">
              <FileText className="h-3.5 w-3.5" />
              <span className="hidden md:inline">Coda fatturazione</span>
              <span className="md:hidden">Coda</span>
            </TabsItem>
            <TabsItem value="issued" className="flex items-center justify-center gap-1.5 text-xs min-h-[32px] data-[state=active]:bg-white data-[state=active]:shadow-sm dark:data-[state=active]:bg-gray-700 rounded">
              <Receipt className="h-3.5 w-3.5" />
              <span className="hidden md:inline">Fatture emesse</span>
              <span className="md:hidden">Emesse</span>
            </TabsItem>
          </TabsList>

          {/* Tab: Coda Fatturazione */}
          <TabsContent value="queue" className="mt-4">
            <EnhancedInvoiceQueue dealId={dealId} />
          </TabsContent>

          {/* Tab: Fatture Emesse */}
          <TabsContent value="issued" className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-[#37352F] dark:text-white">Fatture emesse</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={loadIssuedInvoices}
                disabled={loadingInvoices}
                className="h-7 text-xs px-2.5"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loadingInvoices ? 'animate-spin' : ''}`} />
                <span className="hidden md:inline ml-1">Aggiorna</span>
              </Button>
            </div>

            {loadingInvoices ? (
            <div className="flex items-center justify-center py-6">
              <RefreshCw className="h-5 w-5 text-[#2383E2] animate-spin" />
            </div>
          ) : issuedInvoices.length === 0 ? (
            <div className="text-center py-6 bg-[#FBFBFA] dark:bg-gray-800/50 rounded-lg border border-[rgba(55,53,47,0.09)] dark:border-gray-700">
              <Receipt className="h-6 w-6 mx-auto text-[#787774]/50 mb-2" />
              <p className="text-xs text-[#787774] dark:text-gray-400">Nessuna fattura emessa per questa offerta</p>
            </div>
          ) : (
            <div className="space-y-0">
              {issuedInvoices.map((invoice, index) => (
                <div
                  key={invoice.id}
                  className={`py-3 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.04] transition-colors ${index > 0 ? 'border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700' : ''}`}
                >
                  <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-2 md:gap-0">
                    <div className="flex-1">
                      {/* Invoice Header - Notion style */}
                      <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                        <FileText className="h-3.5 w-3.5 text-[#2383E2]" />
                        <span className="text-sm font-medium text-[#37352F] dark:text-white">
                          {getDocumentTypeLabel(invoice.type)} {invoice.number || 'N/A'}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${getStatusColor(invoice.status)}`}>
                          {getStatusLabel(invoice.status)}
                        </span>
                        {/* Taxation type badge */}
                        {(() => {
                          const taxation = getTaxationDisplay(invoice);
                          return (
                            <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${taxation.bgColor} ${taxation.color}`}>
                              {taxation.label}
                            </span>
                          );
                        })()}
                      </div>

                      {/* Invoice Details - Notion style compact */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 text-xs mt-2">
                        <div>
                          <div className="text-[10px] text-[#787774] dark:text-gray-400 uppercase">Data</div>
                          <div className="text-[#37352F] dark:text-white">
                            {invoice.date ? format(new Date(invoice.date), 'dd/MM/yyyy') : 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-[#787774] dark:text-gray-400 uppercase">Imponibile</div>
                          {(() => {
                            // FIX F-07: scorpora gli esenti art.15 dal netto FIC
                            const exempt = getInvoiceExemptAmount(invoice);
                            const taxable = Math.max(0, (invoice.net_amount || 0) - exempt);
                            return (
                              <div className="text-[#37352F] dark:text-white">
                                {formatCurrency(taxable)}
                                {exempt > 0.01 && (
                                  <div className="text-[10px] text-[#787774] dark:text-gray-400">
                                    + esenti {formatCurrency(exempt)}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        <div>
                          <div className="text-[10px] text-[#787774] dark:text-gray-400 uppercase">Totale</div>
                          <div className="font-medium text-[#2383E2] dark:text-blue-400">
                            {formatCurrency(invoice.amount || 0)}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-[#787774] dark:text-gray-400 uppercase">Pagato</div>
                          <div className={`${invoice.paid_amount > 0 ? 'text-[#0F7B6C] dark:text-green-400' : 'text-[#787774] dark:text-gray-400'}`}>
                            {formatCurrency(invoice.paid_amount || 0)}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-[#787774] dark:text-gray-400 uppercase">Residuo</div>
                          <div className={`${(invoice.amount - invoice.paid_amount) > 0 ? 'text-[#D9730D] dark:text-orange-400' : 'text-[#787774] dark:text-gray-400'}`}>
                            {formatCurrency((invoice.amount || 0) - (invoice.paid_amount || 0))}
                          </div>
                        </div>
                      </div>

                      {/* Payment Info - Notion style */}
                      {invoice.fattureincloud_payments && invoice.fattureincloud_payments.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-[rgba(55,53,47,0.06)] dark:border-gray-700/50">
                          <div className="text-[11px] text-[#787774] dark:text-gray-400 mb-1 flex items-center gap-1">
                            <CreditCard className="h-3 w-3" />
                            Pagamenti:
                          </div>
                          <div className="space-y-0.5">
                            {invoice.fattureincloud_payments.map((payment) => (
                              <div key={payment.id} className="text-xs flex justify-between items-center">
                                <span className="text-[#787774] dark:text-gray-400">
                                  {payment.date ? format(new Date(payment.date), 'dd/MM/yyyy') : 'N/A'} - {payment.payment_method || 'N/A'}
                                </span>
                                <span className="text-[#0F7B6C] dark:text-green-400">
                                  {formatCurrency(payment.amount)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions - Notion style */}
                    <div className="md:ml-3 mt-1 md:mt-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                        className="h-7 text-xs px-2"
                      >
                        <a
                          href={`https://secure.fattureincloud.it/invoices?type=${invoice.type}&id=${invoice.fattureincloud_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          <span className="hidden md:inline">Apri</span>
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}